import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';

import LoadingIndicator from '../../components/loading-indicator/loading';

import ProjectsService from '../../api/services/ProjectsService';
import ItemsService from '../../api/services/ItemsService';
import { loadProjects } from '../../redux/actions/projectActions';
import { loadFacilities } from '../../redux/actions/facilityActions';
import Dropdown from '../../components/data-dropdown/dropdown';

function Inventory({
    facilities,
    projects,
    loadFacilities,
    loadProjects
}) {
    const [facilityId, setFacilityId] = useState(1);
    const [sourceProjectId, setSourceProjectId] = useState('');
    const [destinationProjectId, setDestinationProjectId] = useState('');
    const [destinationProjectHasItems, setDestinationProjectHasItems] = useState(false)
    const [skusWithoutLocations, setSkusWithoutLocations] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => { 
        loadFacilities(sourceProjectId).catch(error => {
            alert('Loading facilities failed: ' + error)
        });
    }, [sourceProjectId]);

    useEffect(() => {
        loadProjects(facilityId).catch(error => {
            alert('Loading projects failed: ' + error)
        });
    }, [facilityId]);

    useEffect(() => {
        setDestinationProjectHasItems(false);

        if (destinationProjectId) {
            getDestinationProjectItemCount();
        }
    }, [facilityId, sourceProjectId, destinationProjectId])

    const getDestinationProjectItemCount = () => {
        if (destinationProjectId !== '') {
            ProjectsService.getItemCountForProject(destinationProjectId).then(result => {
                setDestinationProjectHasItems(result.count > 0);
            })
        }
    };

    const facilityHandleChange = (event) => {
        setFacilityId(event.target.value);
    };

    const sourceProjectHandleChange = (event) => {
        setSourceProjectId(event.target.value);
    };

    const destinationProjectHandleChange = (event) => {
        setDestinationProjectId(event.target.value);
    };

    const validate = () => {
        const errors = [];
        if (sourceProjectId === '' || destinationProjectId === '') {
            errors.push('A source and destination project are required.');
        }

        if (sourceProjectId === destinationProjectId) {
            errors.push('Destination cannot be the same as the source.');
        }

        if (!facilityId || facilityId.length === 0) {
            errors.push('A warehouse selection is required.');
        }

        // NOTE: We were checking for existing items in the destination customer
        // But as of W3PL-29, we are "merging" items

        return errors;
    }

    const reset = () => {
        setSourceProjectId('');
        setDestinationProjectId('');
        setDestinationProjectHasItems(false);
    }

    function onSubmitTransfer(event) {
        event.preventDefault();

        const validationErrors = validate();
        if (validationErrors.length > 0) {
            toast.error(validationErrors.join(' \n'))
            return;
        }

        setIsProcessing(true);
        ItemsService.transferItems(sourceProjectId, destinationProjectId, facilityId)
            .then(response => {
                setIsProcessing(false);
                setSkusWithoutLocations(response.skusWithoutLocations);
                toast.success(`${response.itemsTransferred} items have been transferred to the destination project.`);
                reset();
            })
            .catch(error => {
                setIsProcessing(false);
                toast.error('There was an error transferring the items: ' + error);
            })
    }
    return (
        <div>
            <section className="dashboard card">                
                <article>
                    <h1>Transfer Inventory</h1>

                    <form>
                        <Dropdown 
                            name="Warehouse"
                            currentValue={facilityId}
                            handleChange={facilityHandleChange}
                            data={facilities}
                        ></Dropdown>
                        
                        <Dropdown 
                            name="Source Project"
                            currentValue={sourceProjectId}
                            handleChange={sourceProjectHandleChange}
                            data={projects}
                        ></Dropdown>
                        
                        <Dropdown 
                            name="Destination Project"
                            currentValue={destinationProjectId}
                            handleChange={destinationProjectHandleChange}
                            data={projects}
                        ></Dropdown>
                    </form>

                    <br /><br />
                    <Alert severity="info">
                        The transfer process may take a few minutes to complete, depending on the number of items to transfer.
                        Please do not navigate away from this page while the process is running.
                    </Alert>
                    <br /><br />

                    <Button type="submit" variant="contained" color="primary" onClick={onSubmitTransfer} disabled={isProcessing}>
                        Transfer
                    </Button>
                    
                    <LoadingIndicator visible={isProcessing} />

                    {skusWithoutLocations.length > 0 ? (
                        <>
                            <br /><br />
                            <Alert severity="warning">
                                The following SKUs had no location defined and were moved to &quot;NOF.&quot;&nbsp;
                                <b>
                                    Please review these items and assign proper locations.
                                </b>

                                <ul>
                                    {skusWithoutLocations.map(sku => (<li key={sku}>{sku}</li>))}
                                </ul>
                            </Alert>
                        </>
                    )
                    : null}
                </article>
            </section>
        </div>
    )
}

Inventory.propTypes = {
    facilities: PropTypes.array.isRequired,
    projects: PropTypes.array.isRequired,
    loadFacilities: PropTypes.func.isRequired,
    loadProjects: PropTypes.func.isRequired
}

function mapStateToProps(state) {
    return {
        facilities: state.facilities,
        projects: state.projects
    }
}

const mapDispatchToProps = {
    loadFacilities,
    loadProjects
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Inventory);